import {
  cn
} from "./chunk-PRYE6Q3K.js";

// src/components/ui/collapsible.tsx
import { Collapsible as CollapsiblePrimitive } from "radix-ui";
import { jsx } from "react/jsx-runtime";
function Collapsible({
  ...props
}) {
  return /* @__PURE__ */ jsx(CollapsiblePrimitive.Root, { "data-slot": "collapsible", ...props });
}
function CollapsibleTrigger({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    CollapsiblePrimitive.CollapsibleTrigger,
    {
      "data-slot": "collapsible-trigger",
      ...props
    }
  );
}
function CollapsibleContent({
  ...props
}) {
  return /* @__PURE__ */ jsx(
    CollapsiblePrimitive.CollapsibleContent,
    {
      "data-slot": "collapsible-content",
      ...props
    }
  );
}

// src/components/ui/toggle.tsx
import "react";
import { cva } from "class-variance-authority";
import { Toggle as TogglePrimitive } from "radix-ui";
import { jsx as jsx2 } from "react/jsx-runtime";
var toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-[color,box-shadow] outline-none hover:bg-muted hover:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline: "border border-input bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground"
      },
      size: {
        default: "h-9 min-w-9 px-2",
        sm: "h-8 min-w-8 px-1.5",
        lg: "h-10 min-w-10 px-2.5"
      }
    },
    defaultVariants: {
      variant: "default",
      size: "default"
    }
  }
);
function Toggle({
  className,
  variant,
  size,
  ...props
}) {
  return /* @__PURE__ */ jsx2(
    TogglePrimitive.Root,
    {
      "data-slot": "toggle",
      className: cn(toggleVariants({ variant, size, className })),
      ...props
    }
  );
}

// src/components/step-log.tsx
import * as React2 from "react";
import { CheckIcon } from "lucide-react";
import { animate, useReducedMotion } from "motion/react";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var EASE_OUT = [0.22, 1, 0.36, 1];
var STICK_TO_BOTTOM_PX = 120;
function StepLog({ steps, className, follow, detailClassName, metaClassName, onScroll: onScrollProp, ref: forwardedRef, ...props }) {
  const reduceMotion = useReducedMotion();
  const scrollRef = React2.useRef(null);
  const stuck = React2.useRef(true);
  const auto = React2.useRef(false);
  const settled = React2.useRef(false);
  const initialCount = React2.useRef(steps.length).current;
  const activeIndex = steps.findIndex((s) => (s.status ?? "pending") === "active");
  const setRef = React2.useCallback(
    (node) => {
      scrollRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef]
  );
  const handleScroll = (e) => {
    onScrollProp?.(e);
    const el = scrollRef.current;
    if (!follow || !el || auto.current) return;
    stuck.current = el.scrollHeight - el.scrollTop - el.clientHeight < STICK_TO_BOTTOM_PX;
  };
  React2.useEffect(() => {
    const el = scrollRef.current;
    if (!follow || !el) return;
    if (!stuck.current) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    let target = maxScroll;
    if (follow === "active") {
      const active = el.querySelector('[data-status="active"]');
      if (active) {
        const offset = active.getBoundingClientRect().top - el.getBoundingClientRect().top;
        target = el.scrollTop + offset - (el.clientHeight - active.clientHeight) / 2;
      }
    }
    target = Math.max(0, Math.min(target, maxScroll));
    if (reduceMotion || !settled.current) {
      settled.current = true;
      el.scrollTop = target;
      return;
    }
    auto.current = true;
    const controls = animate(el.scrollTop, target, {
      duration: 0.45,
      ease: EASE_OUT,
      onUpdate: (v) => {
        el.scrollTop = v;
      },
      onComplete: () => {
        auto.current = false;
      }
    });
    return () => {
      auto.current = false;
      controls.stop();
    };
  }, [follow, reduceMotion, steps.length, activeIndex]);
  return /* @__PURE__ */ jsx3(
    "ol",
    {
      ref: setRef,
      "data-slot": "step-log",
      onScroll: handleScroll,
      className: cn("flex flex-col text-sm", follow && "overflow-y-auto p-1 scroll-fade", className),
      ...props,
      children: steps.map((s, i) => {
        const status = s.status ?? "pending";
        const last = i === steps.length - 1;
        const isNew = follow && !reduceMotion && i >= initialCount;
        return /* @__PURE__ */ jsxs(
          "li",
          {
            "data-status": status,
            className: cn("flex gap-3", isNew && "animate-in fade-in slide-in-from-bottom-2 duration-300"),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex flex-col items-center", children: [
                /* @__PURE__ */ jsx3(
                  "span",
                  {
                    className: cn(
                      "z-10 flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                      status === "done" ? "bg-primary text-primary-foreground" : status === "active" ? "bg-primary/15 text-primary ring-2 ring-primary/30" : status === "error" ? "bg-destructive text-destructive-foreground" : "bg-muted text-muted-foreground"
                    ),
                    children: status === "done" ? /* @__PURE__ */ jsx3(CheckIcon, { className: "size-3" }) : status === "error" ? "!" : i + 1
                  }
                ),
                !last && /* @__PURE__ */ jsx3("span", { className: "w-px flex-1 bg-border" })
              ] }),
              /* @__PURE__ */ jsxs("div", { className: cn("pb-5", last && "pb-0"), children: [
                /* @__PURE__ */ jsx3("div", { className: "font-medium text-foreground", children: s.label }),
                s.detail && /* @__PURE__ */ jsx3("div", { className: cn("mt-0.5 text-muted-foreground", detailClassName), children: s.detail }),
                s.meta != null && s.meta !== false && /* @__PURE__ */ jsx3("div", { className: cn("mt-0.5 font-mono text-muted-foreground", metaClassName), children: s.meta }),
                s.note != null && s.note !== false && /* @__PURE__ */ jsx3("div", { className: "mt-0.5 text-muted-foreground", children: s.note })
              ] })
            ]
          },
          i
        );
      })
    }
  );
}

export {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  toggleVariants,
  Toggle,
  StepLog
};
//# sourceMappingURL=chunk-GSDDQVLY.js.map